/** Number and time formatting. Centralised so the board is consistent. */

const NF = new Intl.NumberFormat('en-US')

export const n = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : NF.format(v)

/** Compact form for headline figures where the exact digit does not matter. */
export function compact(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const a = Math.abs(v)
  if (a >= 1e12) return (v / 1e12).toFixed(1).replace(/\.0$/, '') + 'T'
  if (a >= 1e9) return (v / 1e9).toFixed(1).replace(/\.0$/, '') + 'B'
  if (a >= 1e6) return (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'
  if (a >= 1e3) return (v / 1e3).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(v)
}

/** Epoch ms for an API timestamp. The Qdrant metrics store returns naive UTC
 *  ("2026-11-01T13:42:53.835"), and `Date.parse` reads a naive ISO string as
 *  LOCAL time: in IST every client's "last seen" read 5h ago seconds after it
 *  was seen. A timestamp with no zone is UTC here. */
export function parseUtc(iso: string): number {
  return Date.parse(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`)
}

/**
 * Headline figures: exact while they fit, rounded once they do not.
 *
 * "206,891,670" is eleven characters and does not fit a stat cell at headline
 * size. It first overflowed onto its neighbour; once clipped it rendered as
 * "206,891…", which is worse than rounding -- a truncated number reads as a
 * much smaller one. "206.9M" is visibly approximate AND fits.
 *
 * The threshold is 1e6, not lower, because six digits with separators
 * ("922,798") still fit comfortably and an exact figure beats a rounded one
 * whenever it can be shown. Callers keep the exact value in a title attribute,
 * so precision is never actually lost -- only deferred to a hover.
 */
export function headline(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return Math.abs(v) >= 1e6 ? compact(v) : n(v)
}

export const pct = (v: number | null | undefined, dp = 1): string =>
  v === null || v === undefined ? '—' : `${v.toFixed(dp)}%`

/**
 * Millisecond latencies. Widens to two decimals under 10ms for the same
 * reason `rate()` (Embeddings.tsx) and `usd()` widen below their own
 * thresholds — a query that genuinely completes in 3.2ms should not collapse
 * to the same "3ms" a 3.7ms query would also print.
 */
export function ms(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return `${(Math.abs(v) < 10 ? v.toFixed(2) : v.toFixed(1))}ms`
}

/**
 * USD, at whatever precision keeps a sub-cent figure legible.
 *
 * The embeddings mapper's real spend is fractions of a cent -- an early
 * sample cost $0.0006 for 6,515 tokens -- and a bare two-decimal format prints
 * "$0.00" for every number that pipeline actually produces, which reads as
 * free when it is not. Below a dollar this widens to two SIGNIFICANT digits
 * rather than two decimal places, so the true order of magnitude survives; at
 * or above a dollar it is ordinary two-decimal currency, because a large
 * cumulative spend does not need six decimal places to be legible.
 */
const USD_WHOLE = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const USD_FRACTIONAL = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumSignificantDigits: 2, maximumSignificantDigits: 2,
})
export function usd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  if (v === 0) return '$0.00'
  return (Math.abs(v) < 1 ? USD_FRACTIONAL : USD_WHOLE).format(v)
}

/**
 * Ages read as "11d", never as a raw second count. Every snapshot-backed panel
 * shows one, because a stale number that looks live is the failure this whole
 * design exists to prevent.
 */
export function age(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}
